/*
 * tc-carousel-buttons.js: a carousel's Previous and Next keep keyboard focus when they reach the end
 * (shopify-theme #654, WCAG 2.4.3). One copy fixes every carousel on the page, and a page that loads
 * it twice runs it once.
 *
 * Loaded by the sections whose carousels do not wrap and are on pages customers reach (2026-10-19):
 * section-collection-tabs (the 404 page), section-collection-slider (the PDP cross-sell),
 * cart-upsell-lazy (the cart upsell; the markup it fetches cannot run scripts) and section-gallery.
 * The other sections that can build one are disabled, not placed, or only on the unpublished
 * lookbook page (which loads this file through its collection-tabs and collection-slider): related
 * with products-recommendation, products-recently-viewed, section-list-collections with a desktop
 * carousel, section-blog, and the collection blocks of section-custom-content and section-double.
 * Give a section the same script tag if one of those is turned on.
 *
 * What went wrong (measured on the published theme, 404 page, Best Sellers: 6 cards, 4 in view):
 * - Next. theme.js's <flickity-carousel> (Carousel.lockArrows, on Flickity's change event) sets the
 *   button's disabled property once the index reaches slidesTotal - slidesVisible, which is the
 *   second press. Nothing moves focus first. Chrome blurs a focused button that turns disabled a
 *   moment later (activeElement is still the button right after the write), so focus falls to body
 *   and NVDA says only "unavailable".
 * - Previous. Flickity's own updatePrevNextButton (assets/vendor.js) disables it at index 0. Its
 *   guard does work, because Chrome blurs late: it sees the button still focused and moves focus to
 *   the carousel's own Tab stop, and NVDA then reads every card on screen (about 3,200 characters).
 *   The guard never runs for Next there: Flickity's own last index is slides.length - 1 (5), and the
 *   theme ends Next earlier because contain: true parks indexes 2 to 5 at the same position.
 *
 * The remedy, on every Flickity instance without wrapAround: the end state is aria-disabled="true"
 * instead of the disabled attribute. The button stays where it is in the Tab order and keeps focus,
 * NVDA says "Next, button, unavailable", and a press does nothing. Without editing vendor.js or
 * theme.js:
 * - Flickity's constructor is window.themeVendor.Flickity (FlickityFade is the same object, so the
 *   slideshow's instances share the prototype). Its updatePrevNextButton is replaced, for instances
 *   without wrapAround, by the same logic minus the focus move: the button keeps focus now, and
 *   moving it would only send a screen reader through every card.
 * - Each such button gets its own disabled property: reading it reports aria-disabled, writing it
 *   sets or clears aria-disabled. theme.js's lockArrows writes it and toggleArrows reads it (both
 *   ends reached means nothing to scroll, so the arrows hide), so both keep working unchanged.
 * - A press on an aria-disabled button (a click, Enter, Space) is stopped at the document in the
 *   capture phase, before Flickity's click listener on the button hears it, and so is the arrow key
 *   that points the same way (Flickity moves on ArrowLeft and ArrowRight while a button has focus).
 *   Otherwise Next at the theme's end would still advance Flickity's index, and Previous would then
 *   need extra presses before the carousel moved (TC autoreview on 3fbe19e). The same overshoot from
 *   the carousel's own Tab stop and from a drag is stopped by the select clamp below (#660).
 * - custom-css.css gives the state the look of theme.css's .flickity-button:disabled. A focused
 *   button keeps its full focus ring; only its arrow fades.
 * Buttons already on the page when this runs are converted at once. Later ones (an instance built
 * later, a breakpoint rebuild, watchCSS turning a carousel back on, the theme editor) are converted
 * the first time Flickity updates them, which is the select event that comes before every change.
 * Carousels with wrapAround keep Flickity's own behaviour: their buttons only disable when there is
 * a single slide.
 *
 * The end of a theme carousel (#660). Flickity's index ran on past the theme's end with nothing
 * moving: ArrowRight on the carousel's own Tab stop (Flickity's keyboard handler calls next() while
 * the carousel or either button has focus), and a drag, whose resting select is often a later slide
 * (theme.js has containDrag() for that, but never calls it: resolution$1.isMobile||this.containDrag()
 * tests the function, which is always truthy). The next one to three ArrowLeft or Previous presses
 * then moved nothing (measured on the published theme at 1440 and 1024 on all four sections above).
 * And Flickity enables Next on every select event while only lockArrows, on change, ends it, so a
 * select that kept the index at the end (Flickity's own resize) left Next available, doing nothing.
 * So, on a <flickity-carousel>'s own instance without wrapAround or groupCells:
 * - select() past the end selects the end. The end is the theme's (slidesTotal - slidesVisible,
 *   where lockArrows ends Next), but never short of the first index contain parks at the last
 *   position, so no card is ever left out of reach. The two agreed on every live carousel measured.
 *   Selecting the end shows the same cards as the later index would have (contain parks them all at
 *   one position), so tc-carousel-exposure.js still reveals a focused card with selectCell.
 * - After every select, Next ends exactly at that same end (lockArrows' rule, with the clamp's end),
 *   so Next's state follows the index even when the index did not change, and a pointer reaches
 *   every position the keyboard does (Codex review on f638228). Except during a free-scroll rest (a
 *   drag that stops between two positions): the carousel is not pulled to the selected slide then,
 *   so Next stays as Flickity and the theme leave it.
 * Other instances (wrapping, grouped, built by the theme's Slider or the product gallery) and isWrap
 * selects (autoPlay; no live carousel uses it) go through to Flickity unchanged.
 */
(function () {
  if (window.TCCarouselButtons) return;

  var native = Object.getOwnPropertyDescriptor(HTMLButtonElement.prototype, 'disabled');

  // One of Flickity's PrevNextButton objects ({ element, enable(), disable() }). Idempotent.
  function convert(button) {
    var el = button && button.element;
    if (!el || Object.prototype.hasOwnProperty.call(el, 'disabled')) return;
    var wasDisabled = el.hasAttribute('disabled');
    Object.defineProperty(el, 'disabled', {
      configurable: true,
      get: function () { return el.getAttribute('aria-disabled') === 'true'; },
      set: function (off) {
        if (off) el.setAttribute('aria-disabled', 'true');
        else el.removeAttribute('aria-disabled');
      }
    });
    // Flickity's own enable() and disable() write the attribute directly.
    button.enable = function () { el.disabled = false; };
    button.disable = function () { el.disabled = true; };
    if (wasDisabled) {
      native.set.call(el, false);
      el.disabled = true;
    }
  }

  // The <flickity-carousel> whose own carousel a Flickity instance is, if it neither wraps nor groups.
  function owner(flkty) {
    var el = flkty.element;
    var host = el && el.closest ? el.closest('flickity-carousel') : null;
    if (!host || host.carousel !== el || flkty.options.wrapAround || flkty.options.groupCells) return null;
    return host;
  }

  // The last index worth selecting, or -1 when it cannot be told.
  function lastIndex(flkty, host) {
    var slides = flkty.slides || [];
    var last = slides.length - 1;
    var end = host.slidesTotal - host.slidesVisible;
    if (last < 1 || typeof end !== 'number' || isNaN(end)) return -1;
    var parked = last;
    while (parked > 0 && Math.abs(slides[parked - 1].target - slides[last].target) < 1) parked -= 1;
    return Math.min(last, Math.max(end, parked));
  }

  function start() {
    var Flickity = window.themeVendor && window.themeVendor.Flickity;
    var proto = Flickity && Flickity.prototype;
    if (!native || !proto || typeof proto.updatePrevNextButton !== 'function' || typeof Flickity.data !== 'function') return false;

    var update = proto.updatePrevNextButton;
    proto.updatePrevNextButton = function (button, disabledIndex) {
      if (this.options.wrapAround) return update.apply(this, arguments);
      convert(button);
      button[this.selectedIndex !== disabledIndex ? 'enable' : 'disable']();
    };

    // #660: a theme carousel's index stops at its end, and Next's state follows every select.
    var select = proto.select;
    if (typeof select === 'function') {
      proto.select = function (index, isWrap, isInstant) {
        var host = owner(this);
        if (!host || isWrap) return select.apply(this, arguments);
        var end = lastIndex(this, host);
        if (end >= 0 && parseInt(index, 10) > end) index = end;
        select.call(this, index, isWrap, isInstant);
        // Not while Flickity's constructor runs its first select: the theme holds neither yet. Not
        // during a free-scroll rest either (a drag's select that leaves the carousel where the glide
        // ends, not at the selected slide): a short drag back from the end keeps index 2 with part of
        // the last card cut, and Next must stay available to finish the move (TC autoreview on
        // c5878dd). Pressing it ends the free scroll (uiChange), and the clamped select then ends Next.
        // The write is lockArrows' own (carouselNext.disabled, which #654 turned into aria-disabled).
        if (end >= 0 && this.isActive && !this.isFreeScrolling && host.carouselInstance === this && host.carouselNext) host.carouselNext.disabled = this.selectedIndex >= end;
      };
    }

    var unavailable = function (t) {
      var b = t && t.closest ? t.closest('.flickity-prev-next-button') : null;
      return b && b.getAttribute('aria-disabled') === 'true' ? b : null;
    };
    document.addEventListener('click', function (e) {
      if (!unavailable(e.target)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }, true);
    // Flickity also moves on ArrowLeft and ArrowRight while focus is on either button. On a
    // button that has reached its end, the arrow that points the same way is stopped too:
    // Flickity's own last index is later than the theme's, so it would move the index on with
    // nothing moving on screen. The other arrow still works.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      var b = unavailable(e.target);
      var flkty = b && b.parentElement && Flickity.data(b.parentElement);
      if (!flkty) return;
      var forward = (e.key === 'ArrowRight') !== !!flkty.options.rightToLeft;
      if (forward !== b.classList.contains('next')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    }, true);

    document.querySelectorAll('.flickity-prev-next-button').forEach(function (el) {
      var flkty = el.parentElement && Flickity.data(el.parentElement);
      if (!flkty || flkty.options.wrapAround) return;
      convert(flkty.prevButton);
      convert(flkty.nextButton);
    });
    return true;
  }

  window.TCCarouselButtons = { convert: convert };
  // vendor.js is deferred in the head, so it has run before this deferred file; the second try
  // covers a page that loads this file some other way.
  if (!start() && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  }
})();
